import React, { useEffect, useRef, useState, useCallback, } from 'react';
import {useLocation} from 'react-router-dom';
import Immutable from 'immutable';
import moment from 'moment/moment.js';
import {EditorState, KeyBindingUtil,} from 'draft-js';
import {convertFromHTML} from 'draft-convert';
import {ENTITY_TYPE} from 'draftail';
import {
    EDITOR_DESCRIPTIONS, EDITOR_LABELS, EDITOR_KEYBOARD_SHORTCUTS,
} from "../constants";
import platform from "platform";
const { isOptionKeyCommand } = KeyBindingUtil;

moment.locale('de');

export function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useDebouncedEffect(effect, delay, deps) {
    const callback = useCallback(effect, deps);

    useEffect(() => {
        const handler = setTimeout(() => {
            callback();
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [callback, delay]);
};

export function useForceUpdate() {
    const [_, updateState] = useState();
    return useCallback(() => updateState({}), []);
}

export function timeSince(date) {
  return moment(date).fromNow();
}

export function formatDate(date, format="L") {
  return moment(date).format(format);
}

export function lifetime(date, unit="seconds") {
  return moment().diff(moment(date), unit);
}

export function forwardRefAs(As) {
  return ({ forwardedRef, ...props }) => (
    <As ref={forwardedRef} {...props} />
  );
}

export const DivRef = forwardRefAs('div');
export const LiRef = forwardRefAs('li');
export const FormRef = forwardRefAs('form');
export const InputRef = forwardRefAs('input');

export function isDefined(v) {
    return v !== null && v !== undefined;
}

export function isPresent(v) {
    return isDefined(v) && v.length > 0;
}

export function isFunction(f) {
    return !!(f && f.constructor && f.call && f.apply);
};

export function errTrans(_msg) {
    if(typeof _msg !== "string") {
        return _msg;
    }
    const msg = `${_msg}`;
    if (msg.match(/.*authenti.*/)) {
        return "Du bist nicht angemeldet."
    } else if(msg.match(/^.*token malformed.*$/) ||
        msg.match(/^.*[Ss]ignature verification raised.*$/)) {
        return "Es gab ein Problem mit der Sitzung. Bitte logge dich erneut "+
               "ein. Deine Sitzung wurde vorsichtshalber beendet. "
    } else if(msg.match(/^.*not authorized.*$/)) {
        return "Dir fehlen Berechtigungen für diese Aktion."
    } else if(msg.match(/^.*[fF]ailed\sto\sfetch.*$/)) {
        return "Der Server ist nicht erreichbar. Versuchs später nochmal."
    } else if(msg.match(/^.*[Ss]ignature\shas\sexpired.*$/)) {
        return "Die Sitzung ist abgelaufen und kann nicht wiederhergestellt "+
               "werden. Bitte logge dich erneut ein."
    } else if(msg.match(/^.*revoked token*$/)) {
        return "Deine Sitzung wurde beendet. Bitte logge dich erneut ein."
    } else {
        return msg;
    }
}

export function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
}

export function useSubset(imap, keyArray, setter) {
    return [imap.getIn(keyArray), (v) => setter(imap.setIn(keyArray, v))]
}

export function randomInt(maxInt) {
    return Math.floor(Math.random() * maxInt);
}

export function genId() {
    return Math.random().toString(36).slice(2);
};

export function makeApiListUnique(iList) {
    const indexHash = {};
    const newlist = [];
    for(const item of iList) {
        const slug = item.get('slug');
        if (indexHash.hasOwnProperty(slug)) {
            newlist[indexHash[slug]] = item;
        } else {
            indexHash[slug] = newlist.length;
            newlist.push(item);
        }
    }
    return Immutable.List(newlist);
}

const ROLE_MAP = {
    guest: 0, regular: 10, active: 20, mod: 30, admin: 40,
};
export function canISee(myrole, itemVisibility) {
    return ROLE_MAP[myrole] >= ROLE_MAP[itemVisibility];
}

const HTMLImporterConfig = {
    htmlToEntity: (nodeName, node, createEntity) => {
        if (nodeName === "a") {
            return createEntity(ENTITY_TYPE.LINK, "MUTABLE", { href: node.href });
        }
        if (nodeName === "img") {
            const parentHref =
                (node && node.parentNode && node.parentNode.tagName === "A" && node.parentNode.href) ?
                node.parentNode.href : null;
            return createEntity(ENTITY_TYPE.IMAGE, "MUTABLE", {
                src: node.src,
                alt: node.alt,
                width: node.width,
                height: node.height,
                href: parentHref,
            });
        }
        if (nodeName === "hr") {
            return createEntity(ENTITY_TYPE.HORIZONTAL_RULE, "IMMUTABLE", {});
        }
        //console.log('parse from html, entity skipped:', nodeName, node);
        return null;
    },
    htmlToBlock: (nodeName) => {
        if (nodeName === "hr" || nodeName === "img") {
            return "atomic";
        }
        //console.log('parse from html, block skipped:', nodeName);
        return null;
    },
};

export function fromHTML(htmlContent) {
    return convertFromHTML(HTMLImporterConfig)(htmlContent);
};

export function createEditorStateFromHTML(htmlContent) {
    if(isPresent(htmlContent)) {
        const contentState = fromHTML(htmlContent);
        return EditorState.createWithContent(contentState);
    } else {
        return EditorState.createEmpty();
    }
}

export function mapPageContent(mapWithBlocks, fn) {
    let blocks = mapWithBlocks.get('blocks');
    if(blocks && blocks.size && blocks.size > 0) {
        blocks = blocks.map(b => {
            let block = b;
            const body = block.get('body');
            block = block.set('body', fn(body));
            return mapPageContent(block, fn);
        });
    }
    return mapWithBlocks.set('blocks', blocks);
}

export function serializePageContent(page) {
    return mapPageContent(page,
        (c) => (typeof c === "object") ? JSON.stringify(c) : c
    );
}

export function parsePageContent(page) {
    return mapPageContent(page,(c) => {
        try {
            return JSON.parse(c)
        } catch (e) {
            return c;
        }
    });
}

export function findBlockPath(entries, ids, includeChildren=false) {
    const result = ids.reduce((path, id) => {
        const idx = entries.getIn(path).findIndex(n=>n.get('slug') === id);
        if(idx >= 0) {
            path.push(idx);
            path.push('blocks');
            return path;
        } else {
            return [];
        }
    }, []);

    if(!includeChildren) {
        // remove last 'blocks' path element, as we want to point to the block
        // itself, not its children blocks
        result.pop();
    }

    return result;
};

export const getButtonLabel = (type, config) => {
    const icon = typeof config === "boolean" ? undefined : config.icon;

    if (typeof config.label === "string") {
        return config.label;
    }

    if (typeof icon !== "undefined") {
        return null;
    }

    return EDITOR_LABELS[type];
};

const hasKeyboardShortcut = (type) => {
    return !!EDITOR_KEYBOARD_SHORTCUTS[type];
};

const IS_MAC_OS = isOptionKeyCommand({ altKey: "test" }) === "test";

const getKeyboardShortcut = (type, isMacOS = IS_MAC_OS) => {
    const shortcut = EDITOR_KEYBOARD_SHORTCUTS[type];
    const system = isMacOS ? "macOS" : "other";

    return (shortcut && shortcut[system]) || shortcut;
};

export const getButtonTitle = (type, config) => {
    const description =
        typeof config === "boolean" || typeof config.description === "undefined"
        ? EDITOR_DESCRIPTIONS[type]
        : config.description;
    const hasShortcut = hasKeyboardShortcut(type);
    let title = description;

    if (hasShortcut) {
        const desc = description ? `${description}\n` : "";
        title = `${desc}${getKeyboardShortcut(type)}`;
    }

    return title;
};

export const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

// Props that make a clickable non-button element (a, div, i) work like a
// button for keyboard and screen reader users, without changing its markup
// or styling: focusable by Tab, announced as a button, Enter/Space activate.
export const keyboardClick = (onActivate) => ({
    role: "button",
    tabIndex: 0,
    onKeyDown: (ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            // Keeps a nested control (the delete × on a photo tile) from
            // also activating the tile around it.
            ev.stopPropagation();
            onActivate(ev);
        }
    },
});

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), ' +
    'textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
let modalSeq = 0;

// Spread onto a semantic-ui <Modal>, which on its own is not announced as a
// dialog and leaves focus on the page behind it: keyboard users had to tab
// through the whole page to reach it, and screen readers never noticed it.
// Focus goes to the dialog itself, not its first button, so an Enter meant
// for the page cannot confirm something like a deletion.
export const useAccessibleModal = (label) => {
    const idRef = useRef(null);
    if (idRef.current === null) {
        modalSeq += 1;
        idRef.current = `kd-modal-${modalSeq}`;
    }
    const returnFocusTo = useRef(null);
    // Remembered while rendering, i.e. before the dialog exists: by the time
    // it mounts, a field inside it may already have taken focus (autoFocus).
    const active = typeof document !== "undefined" && document.activeElement;
    if (active && active !== document.body && !active.closest('.ui.modal')) {
        returnFocusTo.current = active;
    }
    const id = idRef.current;
    return {
        id,
        role: "dialog",
        "aria-modal": true,
        "aria-label": label,
        tabIndex: -1,
        onMount: () => {
            const dialog = document.getElementById(id);
            if (!dialog) return;
            // Keep Tab inside the dialog instead of escaping to the page behind.
            dialog.addEventListener('keydown', (ev) => {
                if (ev.key !== 'Tab') return;
                const items = [...dialog.querySelectorAll(FOCUSABLE)]
                    .filter(el => el.offsetParent !== null);
                if (items.length === 0) {
                    ev.preventDefault();
                    return;
                }
                const first = items[0];
                const last = items[items.length - 1];
                const active = document.activeElement;
                if (ev.shiftKey && (active === first || active === dialog)) {
                    ev.preventDefault();
                    last.focus();
                } else if (!ev.shiftKey && active === last) {
                    ev.preventDefault();
                    first.focus();
                }
            });
            if (!dialog.contains(document.activeElement)) {
                dialog.focus();
            }
        },
        onUnmount: () => {
            const target = returnFocusTo.current;
            if (target && document.contains(target)) {
                target.focus();
            }
        },
    };
};

export const fieldErr = (errors, name) => {
    try {
        return errors.get(name).map((v, i) => <p key={i}>{v}</p>).toJS();
    } catch (e) {
        // undefined, not null: semantic-ui sets aria-invalid on any error
        // prop !== undefined, so null announced every field as invalid.
        return undefined;
    }
};

const localeCompareSupportsLocales = () => {
    try {
        "test".localeCompare("seq", "i");
    } catch (e) {
        return e.name === "RangeError";
    }
    return false;
};

export const searchCompare = (a, b) => {
    if (localeCompareSupportsLocales()) {
        return a.localeCompare(b, "de", {sensitivity: "base"});
    } else {
        a.toLowerCase().localeCompare(b.toLowerCase());
    }
}

export const searchInclude = (a, b) => {
    const cmpA = a.toLowerCase();
    const cmpB = b.toLowerCase();
    return cmpA.includes(cmpB);
};

export const uniqBy = (list, key) => {
    return Immutable.OrderedMap(list.map(v=>[v.get(key), v])).valueSeq().toList();
};

export const sortDescBy = (list, key) => {
    return list.sortBy(x=>x.get(key)).reverse();
};

export const compare = (a, b) => {
    if (a === undefined && b === undefined) {
        return 0;
    }

    if (a === undefined) {
        return 1;
    }

    if (b === undefined) {
        return -1;
    }

    return a > b ? 1 : a < b ? -1 : 0;
};

export const sortBy = (list, key) => {
    const kar = Array.isArray(key) ? key : [key];
    return list.sort((a, b) => kar.reduce(
        (val, k) => val === 0 ? compare(a.get(k), b.get(k)) : val, 0
    ));
};

export const genAUD = () => {
    return platform.description || "Unbekanntes System";
};

export function parseDraftContent(content) {
    if (!isDefined(content)) return null;
    if (typeof content === 'object' && content.blocks) return content;
    if (typeof content === 'string') {
        try {
            return JSON.parse(content);
        } catch (e) {
            return null;
        }
    }
    return null;
}

/** Ordered IMAGE entities from Draftail JSON (sidebar banners). */
export function extractBannerImagesFromDraftContent(content) {
    const data = parseDraftContent(content);
    if (!data || !data.blocks) return [];
    const entityMap = data.entityMap || {};
    const images = [];
    data.blocks.forEach((block) => {
        if (block.type !== 'atomic' || !block.entityRanges || !block.entityRanges.length) return;
        const key = String(block.entityRanges[0].key);
        const ent = entityMap[key];
        if (ent && ent.type === 'IMAGE' && ent.data && ent.data.src) {
            images.push({ ...ent.data });
        }
    });
    return images;
}

/** All image URLs on a CMS page (for preloading). */
export function extractImageUrlsFromPage(page) {
    if (!page || !page.get) return [];
    const urls = [];
    const top = page.get('top_image_url');
    const bottom = page.get('bottom_image_url');
    if (isPresent(top)) urls.push(top);
    if (isPresent(bottom)) urls.push(bottom);

    const walk = (blocks) => {
        if (!blocks || !blocks.size) return;
        blocks.forEach((block) => {
            const style = block.get('style');
            if (style) {
                const bg = Immutable.Map.isMap(style) ? style.get('backgroundImage') : style.backgroundImage;
                if (isPresent(bg)) urls.push(bg);
            }
            extractBannerImagesFromDraftContent(block.get('body')).forEach((img) => {
                if (img.src) urls.push(img.src);
            });
            walk(block.get('blocks'));
        });
    };
    walk(page.get('blocks') || Immutable.List());
    return [...new Set(urls)];
}
